import { CdkDragDrop } from '@angular/cdk/drag-drop';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  OnDestroy,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';

import { IndexPath, TableViewComponent, TableViewDataSource } from '../table-view';
import { combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GridViewDataSource } from './grid-view.data-source';
import type { ArrowNavigationDelegate } from '../../directives/arrow-navigation';
import type {
  ContentRect,
  DragSelectionDelegate,
  DragSelectionHitTester,
} from '../../directives/drag-selection';
import { GridViewDelegate } from './grid-view.delegate';
import { VirtualScrollDelegate } from '../virtual-scroll';

export interface GridIndexPath extends IndexPath {
  column: number;
}

@Component({
    selector: 'ts-grid-view',
    templateUrl: './grid-view.component.html',
    styleUrls: ['./grid-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GridViewComponent<T = unknown, R = unknown, H = unknown>
  implements
    OnInit,
    OnChanges,
    OnDestroy,
    AfterViewInit,
    ArrowNavigationDelegate,
    DragSelectionHitTester<GridIndexPath>,
    TableViewDataSource<R, H> {
  @Input() public rowNumeralTemplate: TemplateRef<unknown>;
  @Input() public cellTemplate: TemplateRef<unknown>;
  @Input() public headerTemplate: TemplateRef<unknown>;
  @Input() public loading: boolean;
  @Input() public cellsPerRow = 0;
  @Input() public scrollThreshold = 0;
  @Input() public paddingNodes = 1;
  @Input() public locationRow = -1;
  @Input() public cellSize: number;
  @Input() public cellGap = 0;
  @Input() public connectedDropLists = [];
  @Input() public stickyHeader = true;
  @Input() public dataSource: GridViewDataSource<T, R, H>;
  @Input() public draggingDisabled = false;
  @Input() public dragSelectionEnabled = false;
  @Input() public dragSelectionDelegate: DragSelectionDelegate<GridIndexPath> | null = null;
  @Input() public rowNumeralWidth = 0;
  @Input() public delegate: GridViewDelegate<T>;
  @Input() public scrollDelegate: VirtualScrollDelegate;
  @Input() public arrowNavigationEnabled = true;

  @Output() public readonly connectedDropListChanged = new EventEmitter<string[]>();

  @ViewChild('emptyDropList') emptyDropList: ElementRef<HTMLElement>;

  @ViewChild(TableViewComponent) private tableView?: TableViewComponent<R, H>;

  @ViewChildren('row') rows: QueryList<ElementRef<HTMLElement>>;

  private readonly destroy$ = new Subject<void>();
  private readonly JOIN_SYMBOL = '#';

  public cellsPerSection: number[] = [];
  public rowsPerSection: number[];
  public incompleteRowIndexes: number[][];
  public fullRowIndexes: number[];
  private cellIndexesCache: Map<string, GridIndexPath[]> = new Map();
  private cellCache: Map<string, T> = new Map();
  private headerCache: Map<number, H> = new Map();
  private rowNumberCache: Map<string, R> = new Map();
  private readonly rowsPerSection$ = new ReplaySubject<number[]>(1);
  private readonly rowHeight$ = new ReplaySubject<number>(1);
  private readonly rowGap$ = new ReplaySubject<number>(1);
  private dataSourceSubscription: Subscription | undefined;

  public anchorIndexPath: GridIndexPath | undefined;
  public focusIndexPath: GridIndexPath | undefined;

  constructor(private readonly elementRef: ElementRef<HTMLElement>, private readonly cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.rowHeight$.next(this.cellSize);
    this.rowGap$.next(this.cellGap);
    this.subscribeToDataSource();
  }

  public ngAfterViewInit(): void {
    this.registerDropLists(this.rows);

    this.rows.changes.pipe(takeUntil(this.destroy$)).subscribe((rows) => this.registerDropLists(rows));
  }

  /**
   * ngOnChanges only recalculates sections and rows when the input values change.
   * It avoids unnecessary recalculations by comparing inputs and ensuring changes.
   */
  public ngOnChanges(changes: SimpleChanges) {
    let shouldRecalculateSections = false;

    for (const input in changes) {
      const { previousValue, currentValue } = changes[input];

      if (currentValue instanceof Array && this.arrayEqual(currentValue, previousValue)) continue;
      if (currentValue === previousValue) continue;
      if (currentValue === undefined) continue;

      switch (input) {
        case 'cellsPerRow':
          shouldRecalculateSections = true;
          break;
        case 'cellSize':
          shouldRecalculateSections = true;
          this.rowHeight$.next(this.cellSize);
          break;
        case 'cellGap':
          this.rowGap$.next(this.cellGap);
          break;
        default:
          break;
      }
    }

    if (shouldRecalculateSections) this.calculateSectionsAndCells();
  }

  public ngOnDestroy(): void {
    this.connectedDropListChanged.emit([]);
    this.dataSourceSubscription?.unsubscribe();
    this.rowsPerSection$.complete();
    this.rowHeight$.complete();
    this.rowGap$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onDrop(event: CdkDragDrop<unknown, unknown, GridIndexPath>) {
    const currentIndexPath = this.parseDropListId(event.container.id);
    this.delegate.cellMoved(currentIndexPath);
  }

  public onClicked(event: MouseEvent, indexPath: GridIndexPath, cell: T) {
    event.preventDefault();
    event.stopPropagation();

    const isRangeSelection = this.dragSelectionEnabled && !!this.anchorIndexPath;

    if (isRangeSelection) {
      this.focusIndexPath = indexPath;
      this.rangeSelection(this.anchorIndexPath, indexPath);
    } else {
      this.anchorSelection(indexPath, cell);
    }
  }

  public onDoubleClicked(event: MouseEvent, indexPath: GridIndexPath, cell: T) {
    event.preventDefault();
    event.stopPropagation();
    this.delegate.cellDoubleClicked(indexPath, cell);
  }

  public onContextMenuOpened(event: PointerEvent, indexPath: GridIndexPath, cell: T) {
    event.preventDefault();
    event.stopPropagation();
    this.delegate.cellContextMenu(indexPath, cell, event);
  }

  public arrowNavigated(next: GridIndexPath): void {
    this.tableView?.scrollIndexPathIntoViewIfNeeded(next);

    if (this.dragSelectionEnabled && this.anchorIndexPath) {
      this.focusIndexPath = next;
      this.rangeSelection(this.anchorIndexPath, next);
    } else {
      this.navigateTo(next);
    }
  }

  public hitTest(rect: ContentRect): GridIndexPath[] {
    const tableView = this.tableView;
    const virtualScroll = tableView?.virtualScroll;
    if (!tableView || !virtualScroll) return [];
    if (!this.cellsPerRow || !this.cellSize || !this.rowsPerSection) return [];

    const startFlatRow = virtualScroll.getRowAtContentY(rect.top);
    const endFlatRow = virtualScroll.getRowAtContentY(rect.top + rect.height);

    const leftPad = this.rowNumeralWidth + this.cellGap;
    const rightEdge = rect.left + rect.width;
    if (rightEdge < leftPad) return [];

    const colSpan = this.cellSize + this.cellGap;
    const indexes: GridIndexPath[] = [];

    for (let r = startFlatRow; r <= endFlatRow; r++) {
      if (tableView.isSectionRow(r)) continue;
      const indexPath = tableView.getIndexPathForRow(r);
      if (indexPath.section < 0) continue;

      const isLastRow = indexPath.row === this.rowsPerSection[indexPath.section] - 1;
      const colCount = isLastRow ? this.incompleteRowIndexes[indexPath.section].length : this.cellsPerRow;
      if (colCount <= 0) continue;

      const rawColStart = Math.floor((rect.left - leftPad) / colSpan);
      const rawColEnd = Math.floor((rightEdge - leftPad) / colSpan);
      if (rawColStart >= colCount) continue;
      if (rawColEnd < 0) continue;

      const colStart = Math.max(0, rawColStart);
      const colEnd = Math.min(colCount - 1, rawColEnd);
      for (let column = colStart; column <= colEnd; column++) {
        indexes.push({ section: indexPath.section, row: indexPath.row, column });
      }
    }

    return indexes;
  }

  public resolveCell(indexPath: GridIndexPath) {
    return this.dataSource.getCellForIndexPath(indexPath).pipe(
      tap((cell: T) => this.cellCache.set(this.getCellId(indexPath), cell))
    );
  }

  public getRowsPerSection(): Observable<number[]> {
    return this.rowsPerSection$;
  }

  public getRowHeight(): Observable<number> {
    return this.rowHeight$;
  }

  public getRowGap(): Observable<number> {
    return this.rowGap$;
  }

  public getHeaderForIndexPath(index: number): Observable<H | undefined> {
    return this.dataSource.getHeaderForIndexPath(index).pipe(
      tap((header) => { if (header !== undefined) this.headerCache.set(index, header); })
    );
  }

  public getRowNumberForIndexPath(indexPath: IndexPath): Observable<R> {
    const key = `${indexPath.section}${this.JOIN_SYMBOL}${indexPath.row}`;
    return this.dataSource.getRowNumberForIndexPath(indexPath).pipe(
      tap((rowNumber) => this.rowNumberCache.set(key, rowNumber))
    );
  }

  private getCellId(indexPath: GridIndexPath): string {
    const { section, row, column } = indexPath;
    return [section, row, column].join(this.JOIN_SYMBOL);
  }

  private anchorSelection(indexPath: GridIndexPath, cell: T) {
    this.anchorIndexPath = indexPath;
    this.focusIndexPath = indexPath;
    this.delegate.cellClicked(indexPath, cell);
  }

  // TODO: Optimize this function to avoid unnecessary calculations.
  private rangeSelection(startIndexPath: GridIndexPath, endIndexPath: GridIndexPath) {
    // Normalize into "first" and "last" in reading order (section → row → column)
    const [first, last] =
      this.compareIndexPaths(startIndexPath, endIndexPath) <= 0
        ? [startIndexPath, endIndexPath]
        : [endIndexPath, startIndexPath];

    const minSection = first.section;
    const maxSection = last.section;

    // Helper to get bounds for a section (rows/columns)
    const getRowBounds = (section: number) => {
      if (section === minSection && section === maxSection) {
        return [first.row, last.row];
      } else if (section === minSection) {
        return [first.row, this.rowsPerSection[section] - 1];
      } else if (section === maxSection) {
        return [0, last.row];
      } else {
        return [0, this.rowsPerSection[section] - 1];
      }
    };

    const getColBounds = (section: number, row: number) => {
      const isLastRow = row === this.rowsPerSection[section] - 1;
      const columns = isLastRow ? this.incompleteRowIndexes[section] : this.fullRowIndexes;
      const colCount = Array.isArray(columns) ? columns.length : columns;

      const isFirstRow = section === first.section && row === first.row;
      const isLastSelectedRow = section === last.section && row === last.row;

      if (isFirstRow && isLastSelectedRow) {
        return [first.column, last.column];
      } else if (isFirstRow) {
        return [first.column, colCount - 1];
      } else if (isLastSelectedRow) {
        return [0, last.column];
      } else {
        return [0, colCount - 1];
      }
    };

    const cellIndexes: GridIndexPath[] = [];

    for (let section = minSection; section <= maxSection; section++) {
      const [rowStart, rowEnd] = getRowBounds(section);
      for (let row = rowStart; row <= rowEnd; row++) {
        const [colStart, colEnd] = getColBounds(section, row);
        for (let column = colStart; column <= colEnd; column++) {
          const isLastRow = row === this.rowsPerSection[section] - 1;
          const columnsArr = isLastRow ? this.incompleteRowIndexes[section] : this.fullRowIndexes;
          if (Array.isArray(columnsArr) && columnsArr.length > 0 && column >= columnsArr.length) continue;

          cellIndexes.push({ section, row, column });
        }
      }
    }

    this.delegate.cellsRangeSelected(cellIndexes);
  }

  private navigateTo(indexPath: GridIndexPath): void {
    this.anchorIndexPath = indexPath;
    this.focusIndexPath = indexPath;
    const cached = this.cellCache.get(this.getCellId(indexPath));
    if (cached) {
      this.delegate.cellClicked(indexPath, cached);
      return;
    }
    this.dataSource
      .getCellForIndexPath(indexPath)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((cell) => {
        if (cell) this.delegate.cellClicked(indexPath, cell);
      });
  }

  /**
   * Track rows by index to improve rendering performance.
   */
  public trackByIndex(_column: number, item: GridIndexPath): string {
    return `${item.section}-${item.row}-${item.column}`;
  }

  /**
   * Creates a unique ID for each drop list based on section and row.
   */
  public getDropListId(indexPath: IndexPath): string {
    const elementId = this.elementRef.nativeElement.id ?? 'grid';

    return `${elementId}-${indexPath.section}${this.JOIN_SYMBOL}${indexPath.row}`;
  }

  public getCellIndexes(indexPath: IndexPath): GridIndexPath[] {
    const key = `${indexPath.section}${this.JOIN_SYMBOL}${indexPath.row}`;
    const cached = this.cellIndexesCache.get(key);
    if (cached) return cached;

    const rowsForSection = this.rowsPerSection[indexPath.section];
    const isLastRow = indexPath.row === rowsForSection - 1;
    const columns = isLastRow ? this.incompleteRowIndexes[indexPath.section] : this.fullRowIndexes;

    const result = columns.map((_, columnIndex) => ({
      section: indexPath.section,
      row: indexPath.row,
      column: columnIndex,
    }));

    this.cellIndexesCache.set(key, result);
    return result;
  }

  /**
   * Parses the drop list ID and returns an IndexPath.
   */
  private parseDropListId(id: string): IndexPath {
    const elementId = this.elementRef.nativeElement.id ?? 'grid';

    const [section, row] = id.replace(`${elementId}-`, '').split(this.JOIN_SYMBOL).map(Number);

    return { section, row };
  }

  private subscribeToDataSource(): void {
    this.dataSourceSubscription?.unsubscribe();

    this.dataSourceSubscription = this.dataSource
      .numberOfSections()
      .pipe(
        switchMap((sectionCount) => {
          if (sectionCount === 0) return of([]);
          return combineLatest(
            Array.from({ length: sectionCount }, (_, i) => this.dataSource.numberOfCellsInSection(i))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((counts) => {
        this.cellsPerSection = counts;
        this.calculateSectionsAndCells();
        this.cdr.markForCheck();
      });
  }

  /**
   * Calculates the number of rows per section based on cells per section and cells per row.
   * It also initializes the cellIndexes array.
   */
  private calculateSectionsAndCells() {
    if (!this.cellsPerRow || !this.cellsPerSection) return;

    this.cellIndexesCache.clear();
    this.cellCache.clear();
    this.headerCache.clear();
    this.rowNumberCache.clear();
    this.rowsPerSection = [];
    this.incompleteRowIndexes = [];
    this.fullRowIndexes = new Array(this.cellsPerRow).fill(0);

    for (let i = 0; i < this.cellsPerSection.length; i++) {
      const rowsForSection = Math.ceil(this.cellsPerSection[i] / this.cellsPerRow);
      const lastRowCellCount = this.cellsPerSection[i] - (rowsForSection - 1) * this.cellsPerRow;

      this.rowsPerSection.push(rowsForSection > 0 ? rowsForSection : 0);
      this.incompleteRowIndexes.push(new Array(lastRowCellCount).fill(0));
    }

    this.rowsPerSection$.next(this.rowsPerSection);
  }

  private registerDropLists(rows: QueryList<ElementRef>) {
    const listIds = rows.map((list) => list.nativeElement.id);

    if (listIds.length === 0) listIds.push(this.emptyDropList.nativeElement.id);

    this.connectedDropListChanged.emit(listIds);
  }

  /**
   * Helper function to compare two arrays for equality.
   */
  private compareIndexPaths(a: GridIndexPath, b: GridIndexPath): number {
    if (a.section !== b.section) return a.section - b.section;
    if (a.row !== b.row) return a.row - b.row;
    return a.column - b.column;
  }

  private arrayEqual<T>(a: T[], b: T[]): boolean {
    if (!a || !b) return false;
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }
}
